import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { timesheetService } from '../../services/api';
import { FiX, FiPlus } from 'react-icons/fi';
import { format, parse } from 'date-fns';
import './TimesheetForm.css';

export const TimesheetForm = () => {
  const { date } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const displayDate = date ? parse(date, 'yyyy-MM-dd', new Date()) : new Date();

  const [entries, setEntries] = useState([
    { id: 1, taskName: '', hours: 0.5, description: '' },
  ]);
  const [nextId, setNextId] = useState(2);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [existingTimesheet, setExistingTimesheet] = useState(null);

  useEffect(() => {
    fetchTimesheet();
  }, [date]);

  const fetchTimesheet = async () => {
    if (!date) return;

    setLoading(true);
    try {
      const response = await timesheetService.getAll({
        startDate: date,
        endDate: date,
      });
      const timesheets = response.data.timesheets || [];
      if (timesheets.length > 0) {
        const ts = timesheets[0];
        // Fetch the full timesheet with entries
        const detailResponse = await timesheetService.getById(ts.id);
        const fullTimesheet = detailResponse.data;
        setExistingTimesheet(fullTimesheet);
        setIsSubmitted(fullTimesheet.status === 'submitted');
        const newEntries = fullTimesheet.TimesheetEntries || [];
        if (newEntries.length > 0) {
          setEntries(newEntries);
          setNextId(Math.max(...newEntries.map((e) => e.id || 0)) + 1);
        }
      }
    } catch (err) {
      console.error('Failed to fetch timesheet:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalHours = () => {
    return entries.reduce((sum, entry) => sum + (parseFloat(entry.hours) || 0), 0);
  };

  const validateEntries = () => {
    const errors = [];

    entries.forEach((entry, index) => {
      if (!entry.taskName.trim()) {
        errors.push(`Entry ${index + 1}: Task name is required`);
      }
      const hours = parseFloat(entry.hours);
      if (isNaN(hours) || hours <= 0 || hours > 24) {
        errors.push(`Entry ${index + 1}: Hours must be between 0.5 and 24`);
      }
    });

    const totalHours = calculateTotalHours();
    if (totalHours > 24) {
      errors.push('Total hours cannot exceed 24');
    }

    return errors;
  };

  const handleAddEntry = () => {
    const newEntry = {
      id: nextId,
      taskName: '',
      hours: 0.5,
      description: '',
    };
    setEntries([...entries, newEntry]);
    setNextId(nextId + 1);
  };

  const handleRemoveEntry = (id) => {
    if (entries.length > 1) {
      setEntries(entries.filter((entry) => entry.id !== id));
    }
  };

  const handleEntryChange = (id, field, value) => {
    setEntries(
      entries.map((entry) =>
        entry.id === id ? { ...entry, [field]: value } : entry
      )
    );
  };

  const handleSaveDraft = async () => {
    const errors = validateEntries();
    if (errors.length > 0) {
      setError(errors.join('\n'));
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        date: date || format(new Date(), 'yyyy-MM-dd'),
        entries,
        status: 'draft',
      };

      if (existingTimesheet?.id) {
        await timesheetService.update(existingTimesheet.id, payload);
      } else {
        await timesheetService.create(payload);
      }

      setSuccess('Timesheet saved as draft');
      setTimeout(() => {
        navigate('/timesheet');
      }, 1500);
    } catch (err) {
      setError('Failed to save timesheet');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const errors = validateEntries();
    if (errors.length > 0) {
      setError(errors.join('\n'));
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        date: date || format(new Date(), 'yyyy-MM-dd'),
        entries,
        status: 'submitted',
      };

      if (existingTimesheet?.id) {
        await timesheetService.update(existingTimesheet.id, payload);
      } else {
        await timesheetService.create(payload);
      }

      setSuccess('Timesheet submitted successfully');
      setIsSubmitted(true);
      setTimeout(() => {
        navigate('/timesheet');
      }, 1500);
    } catch (err) {
      setError('Failed to submit timesheet');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const totalHours = calculateTotalHours();

  return (
    <div className="timesheet-form-container">
      <div className="form-header">
        <h1>Timesheet Entry</h1>
        <button
          className="btn-close"
          onClick={() => navigate('/timesheet')}
          disabled={loading}
        >
          ✕
        </button>
      </div>

      <div className="date-display">
        <span className="label">Date:</span>
        <span className="date-value">{format(displayDate, 'EEEE, MMMM d, yyyy')}</span>
      </div>

      {error && (
        <div className="alert alert-error">
          {error.split('\n').map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
      )}

      {success && <div className="alert alert-success">{success}</div>}

      {isSubmitted && (
        <div className="alert alert-info">
          This timesheet has been submitted and is in read-only mode.
        </div>
      )}

      <div className="entries-section">
        <table className="entries-table">
          <thead>
            <tr>
              <th>Task Name</th>
              <th>Hours</th>
              <th>Description</th>
              {!isSubmitted && <th></th>}
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.id}>
                <td>
                  <input
                    type="text"
                    value={entry.taskName}
                    onChange={(e) =>
                      handleEntryChange(entry.id, 'taskName', e.target.value)
                    }
                    placeholder="Enter task name"
                    disabled={isSubmitted}
                    className="task-input"
                  />
                </td>
                <td>
                  <select
                    value={entry.hours}
                    onChange={(e) =>
                      handleEntryChange(entry.id, 'hours', e.target.value)
                    }
                    disabled={isSubmitted}
                    className="hours-select"
                  >
                    {[0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10].map(
                      (h) => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      )
                    )}
                  </select>
                </td>
                <td>
                  <input
                    type="text"
                    value={entry.description}
                    onChange={(e) =>
                      handleEntryChange(entry.id, 'description', e.target.value)
                    }
                    placeholder="Optional description"
                    disabled={isSubmitted}
                    className="description-input"
                  />
                </td>
                <td>
                  {!isSubmitted && entries.length > 1 && (
                    <button
                      className="btn-remove"
                      onClick={() => handleRemoveEntry(entry.id)}
                      disabled={loading}
                      title="Remove entry"
                    >
                      <FiX />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {!isSubmitted && (
          <button
            className="btn btn-secondary add-entry-btn"
            onClick={handleAddEntry}
            disabled={loading}
          >
            <FiPlus /> Add Entry
          </button>
        )}
      </div>

      <div className="totals-section">
        <div className="total-item">
          <span className="label">Total Hours:</span>
          <span className={`total-value ${totalHours > 24 ? 'error' : ''}`}>
            {totalHours.toFixed(1)}
          </span>
        </div>
        {totalHours > 24 && (
          <div className="validation-error">
            Total hours cannot exceed 24 hours per day
          </div>
        )}
      </div>

      {!isSubmitted && (
        <div className="actions">
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/timesheet')}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="btn btn-secondary"
            onClick={handleSaveDraft}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save as Draft'}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loading || totalHours > 24}
          >
            {loading ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      )}

      {isSubmitted && (
        <div className="actions">
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/timesheet')}
          >
            Back to Timesheets
          </button>
        </div>
      )}
    </div>
  );
};

export default TimesheetForm;
